export const purple = {
  100: "#F9F7FD",
  200: "#F3EEFD",
  300: "#DBD0FE",
  400: "#C3B2FF",
  600: "#8559EC",
  700: "#714BD6",
  800: "#6C30D1",
  900: "#5627B0",
} as const;

export const gray = {
  100: "#F8F9FA",
  200: "#F2F3F6",
  300: "#E9EBEF",
  400: "#D1D3DB",
  500: "#AEAFBD",
  600: "#6A6E7A",
  700: "#42454B",
  800: "#242427",
  900: "#121212",
} as const;

export const blue = {
  100: "#ECF3FB",
  700: "#107EE6",
  800: "#0E6EC8",
} as const;

export const red = {
  100: "#FFEDED",
  200: "#FFDEE0",
  400: "#FD979D",
  700: "#E8373F",
  800: "#E52029",
  900: "#C71119",
} as const;

export const green = {
  100: "#F2FBF8",
  700: "#04B675",
  800: "#00AD6E",
  900: "#008051",
} as const;

export const yellow = {
  200: "#F7F3EB",
  600: "#FFC453",
  900: "#886A2F",
} as const;

export const brand = {
  black: "#000000",
  "gray-900": "#121212",
  purple: "#A578FF",
  red: "#E82953",
  pink: "#FFB9C6",
  peach: "#FFD7D5",
  blue: "#7595E6",
  green: "#40C8A4",
  yellow: "#FFD083",
  "yellow-200": "#F7F3EB",
  cream: "#F7F3EB",
  white: "#FFFFFF",
  /**
   * Brand purple gradient (Figma: Brand/brand-gradient-1).
   * Applied at top-left → bottom-right (135deg).
   */
  "gradient-1": "linear-gradient(135deg, #6C30D1 0%, #A930D1 100%)",
} as const;

export const base = {
  white: "#FFFFFF",
  black: "#000000",
} as const;

export const overlay = {
  dark: "rgba(18, 18, 18, 0.6)",
} as const;

export const AllColors = {
  base,
  purple,
  gray,
  blue,
  red,
  green,
  yellow,
  brand,
  overlay,
} as const;

/** Returns `true` if `value` is a CSS gradient string (linear/radial/conic). */
export const isColorGradient = (value: string): boolean =>
  /^(linear-gradient|radial-gradient|conic-gradient)\(/.test(value.trim());

/**
 * Flat colors only (gradients are surfaced via {@link GradientPrimitives}).
 * Used as Tailwind's `theme.colors`.
 */
export const ColorPrimitives: Record<string, string> = Object.entries(
  AllColors,
).reduce(
  (acc, [colorType, colorShades]) => {
    Object.entries(colorShades).forEach(([shade, value]) => {
      if (typeof value === "string" && isColorGradient(value)) return;
      acc[`${colorType}-${shade}`] = value;
    });
    return acc;
  },
  {} as Record<string, string>,
);

/**
 * Gradient values keyed by `${group}-${shade}`. Plugged into Tailwind's
 * `theme.extend.backgroundImage` so they're usable as `tw-bg-*` utilities.
 */
export const GradientPrimitives: Record<string, string> = Object.entries(
  AllColors,
).reduce(
  (acc, [colorType, colorShades]) => {
    Object.entries(colorShades).forEach(([shade, value]) => {
      if (typeof value !== "string" || !isColorGradient(value)) return;
      acc[`${colorType}-${shade}`] = value;
    });
    return acc;
  },
  {} as Record<string, string>,
);
